
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";


export function useSetupGuide() {
  const { user, isAuthenticated } = useAuth();
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  

  useEffect(() => {
    const checkSetupStatus = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        // Check if EULA has been accepted before showing setup guide
        const { data: authData } = await supabase.auth.getUser();
        const eulaAccepted = authData?.user?.user_metadata?.eula_accepted_version;
        if (!eulaAccepted) {
          // EULA not yet accepted, don't show setup guide yet
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("user_preferences")
          .select("setup_completed")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error checking setup status:", error);
          setLoading(false);
          return;
        }

        // If setup not completed, don't show guide yet — EnvironmentSetupScreen handles first-time flow.
        // Only show setup guide if setup IS completed but user may want to re-open it.
        if (!data || data.setup_completed !== true) {
          setShowSetupGuide(false);
          setLoading(false);
          return;
        }

        // Setup is completed — check if user has demo data (meaning env setup ran and they haven't gone live yet)
        // In that case, don't auto-show the setup guide since the user is exploring demo data
        const hasDemoData = (data as any)?.has_demo_data === true;
        setShowSetupGuide(!hasDemoData);

        // Note: Auto-population of defaults and demo data is handled by
        // EnvironmentSetupScreen, not here. This hook only tracks setup state.

        setLoading(false);
      } catch (error) {
        console.error("Error in setup guide check:", error);
        setLoading(false);
      }
    };

    checkSetupStatus();
    setNotificationDismissed(false);

    // Listen for auth state changes (e.g. EULA acceptance updates user_metadata)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'USER_UPDATED') {
        checkSetupStatus();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isAuthenticated, user]);

  const markSetupComplete = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .upsert({ 
          user_id: user.id, 
          setup_completed: true 
        }, { onConflict: 'user_id' });

      if (error) {
        console.error("Error marking setup as complete:", error);
        return false;
      }

      setShowSetupGuide(false);
      return true;
    } catch (error) {
      console.error("Error in marking setup complete:", error);
      return false;
    }
  };

  const dismissNotification = () => {
    setNotificationDismissed(true);
  };

  const permanentlyDismissSetupGuide = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("user_preferences")
        .upsert({ 
          user_id: user.id, 
          setup_completed: true 
        }, { onConflict: 'user_id' });

      if (error) {
        console.error("Error permanently dismissing setup guide:", error);
        return false;
      }

      setShowSetupGuide(false);
      return true;
    } catch (error) {
      console.error("Error in permanently dismissing setup guide:", error);
      return false;
    }
  };

  const openSetupDialog = () => {
    setDialogOpen(true);
  };

  return {
    showSetupGuide,
    setShowSetupGuide,
    markSetupComplete,
    loading,
    notificationDismissed,
    dismissNotification,
    permanentlyDismissSetupGuide,
    dialogOpen,
    setDialogOpen,
    openSetupDialog
  };
}
